'use client'

import { useState } from 'react'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { popModal } from '@/components/modals'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { useClearMyProjectListCache } from '@/hooks/use-clear-swr-cache'
import { useProjectStore } from '@/hooks/use-project-store'

type RenameProjectModalProps = {
  projectId: string
  currentName: string
  onSuccess?: (newName: string) => void
}

const MAX_NAME_LENGTH = 80

export default function RenameProjectModal({
  projectId,
  currentName,
  onSuccess,
}: RenameProjectModalProps) {
  const t = useTranslations('rename-project-modal')
  const [name, setName] = useState(currentName)
  const [isSaving, setIsSaving] = useState(false)
  const { clear: clearProjectsCache } = useClearMyProjectListCache()
  const updateProject = useProjectStore((state) => state.updateProject)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const trimmedName = name.trim()

    if (!trimmedName) {
      toast.error(t('errors.empty'))
      return
    }

    if (trimmedName.length > MAX_NAME_LENGTH) {
      toast.error(t('errors.too_long', { max: MAX_NAME_LENGTH }))
      return
    }

    if (trimmedName === currentName) {
      popModal()
      return
    }

    try {
      setIsSaving(true)

      const formData = new FormData()
      formData.append('projectId', projectId)
      formData.append('projectName', trimmedName)

      const response = await fetch('/api/me/project', {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        throw new Error()
      }

      updateProject({ project_name: trimmedName })
      clearProjectsCache()
      onSuccess?.(trimmedName)
      toast.success(t('success'))
      popModal()
    } catch (error) {
      console.error('Failed to rename project', error)
      toast.error(t('errors.request_failed'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <DialogContent className="sm:max-w-lg border border-white/10 bg-[#151218] text-white">
      <DialogHeader className="space-y-2">
        <DialogTitle className="text-2xl font-semibold">
          {t('title')}
        </DialogTitle>
        <DialogDescription className="text-sm text-white/60">
          {t('description')}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="project-name"
            className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60"
          >
            {t('project_name_label')}
          </Label>
          <Input
            id="project-name"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={MAX_NAME_LENGTH}
            placeholder={t('project_name_placeholder')}
            disabled={isSaving}
            autoFocus
            className="border-white/15 bg-white/[0.03] text-base text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-[#8B5CF6]"
          />
          <p className="text-xs text-white/50">
            {t('character_count', {
              length: name.length,
              max: MAX_NAME_LENGTH,
            })}
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline_2"
            className="w-full sm:w-auto"
            disabled={isSaving}
            onClick={() => popModal()}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            className="w-full bg-white text-black hover:bg-white/90 sm:w-auto"
            disabled={isSaving}
          >
            {isSaving ? t('saving') : t('save')}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
